import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AddMemberModal from "../components/AddMemberModal";
import MembersTable from "../components/MembersTable";
import * as projectsApi from "../../../../core/data/redux/services/projectsApi";
import {
  addProjectMember,
  fetchProjectById,
  fetchProjectMembers,
  removeProjectMember,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectActiveCompany,
  selectCurrentProject,
  selectMembers,
  selectMembersLoading,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { notify } from "../../../../services/toast.config";

import type {
  ProjectMember,
  ProjectMemberRole,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const MembersSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const members = useSelector(selectMembers);
  const loading = useSelector(selectMembersLoading);
  const activeCompany = useSelector(selectActiveCompany);

  const canManageMembers = useProjectCapability(project?.id, "MANAGE_MEMBERS");
  const canChangeRole = useProjectCapability(project?.id, "CHANGE_ROLE");

  const [addOpen, setAddOpen] = useState(false);

  const handleAdd = useCallback(
    async (payload: {
      userId: number;
      role: ProjectMemberRole;
      userSnapshot: Record<string, any>;
    }) => {
      if (!project) throw new Error("No project loaded");
      await dispatch(
        addProjectMember({
          projectId: project.id,
          userId: payload.userId,
          role: payload.role,
          userSnapshot: payload.userSnapshot,
        })
      ).unwrap();
      notify.success("Member added");
      setAddOpen(false);
    },
    [dispatch, project]
  );

  const handleUpdateRole = useCallback(
    async (memberId: number, role: ProjectMemberRole) => {
      try {
        await projectsApi.updateProjectMemberRole(
          activeCompany.company_id,
          activeCompany.company_type,
          memberId,
          role
        );
        if (project) {
          dispatch(fetchProjectMembers(project.id));
          dispatch(fetchProjectById(project.id));
        }
        notify.success("Role updated");
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to update role"));
      }
    },
    [activeCompany, dispatch, project]
  );

  const handleRemove = useCallback(
    async (member: ProjectMember) => {
      try {
        await dispatch(removeProjectMember(member.id)).unwrap();
        if (project) dispatch(fetchProjectById(project.id));
        notify.success("Member removed");
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to remove member"));
      }
    },
    [dispatch, project]
  );

  if (!project) {
    return (
      <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
        <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "2.5rem", height: "2.5rem" }} />
            <p className="mt-3 text-muted mb-0">Loading project…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card border-0" style={{ borderRadius: 14, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
            <i className="ti ti-users-group" style={{ color: "#3b82f6", fontSize: 20, flexShrink: 0 }} />
            <span>Members</span>
            <span className="text-muted small">
              ({members.length} member{members.length === 1 ? "" : "s"})
            </span>
          </h5>
        </div>
        {canManageMembers && (
          <button
            type="button"
            className="btn btn-info btn-sm"
            style={{ borderRadius: 8, whiteSpace: "nowrap" }}
            onClick={() => setAddOpen(true)}
          >
            <i className="ti ti-plus" style={{ fontSize: 16 }} />
            <span>Add Member</span>
          </button>
        )}

      </div>
      <div className="card-body bg-white rounded-3 px-0">
        <MembersTable
          members={members}
          loading={loading}
          canManage={canManageMembers}
          canChangeRole={canChangeRole}
          onUpdateRole={handleUpdateRole}
          onRemove={handleRemove}
        />
      </div>

      {canManageMembers && (
        <AddMemberModal
          open={addOpen}
          excludedUserIds={members.map((m) => m.user_id)}
          onSubmit={handleAdd}
          onCancel={() => setAddOpen(false)}
        />
      )}
    </div>
  );
};

export default MembersSettings;
